/**
 * The outreach CARD — what the home's Recent activity row and its detail say about one conversation with a
 * person (Ronny's report, 2026-10-22: rows read "Messages · <our whole opening message>" / "Recipient | App" /
 * "In progress", the detail said "Waiting on Inbar since 2:18 PM" when Inbar had never written, and a visitor's
 * own words were labelled "<name>'s agent · Reply from : …").
 *
 * Pure and import-free, so it is unit-tested directly. Mirrored 1:1, under the same names, by iOS
 * `OutreachCard.swift` and Android `OutreachCard.kt`; the i18n keys are shared by all three catalogs.
 *
 * The status is derived from REAL facts — who spoke last, and whether the owner has an open decision — never
 * from the raw outreach state, which for a profile-link thread reads WAITING_FOR_YOU from the visitor's first tap.
 */

export type OutreachCardStatus = 'needsYou' | 'aiHandling' | 'waitingForThem' | 'done';

/** The thread's stage GROUP from the contact book (`stageGroup`), when the book holds it. */
export type OutreachCardStageGroup = 'needsYou' | 'inProgress' | 'done';

export interface OutreachCardMessage {
  /** Ours — written by the owner's AI (or the owner) on this thread. */
  mine: boolean;
  text?: string;
  /** ISO time, when the service recorded one. */
  at?: string;
}

export interface OutreachCardInput {
  /** Oldest first. */
  messages: readonly OutreachCardMessage[];
  /** The contact book's stage group for this thread, when it holds it. */
  stageGroup?: OutreachCardStageGroup | null;
  /** A profile-link lead with an open decision for the owner (`leadStatus` = needs_you). */
  leadNeedsYou?: boolean;
  /** The summary's own "still open and waiting on them" — false with our message last means it was closed. */
  awaitingReply?: boolean;
}

/**
 * 1. the book says Done → done;
 * 2. an open decision for the owner (the book's Needs you, or the lead's needs_you) → needsYou;
 * 3. they spoke last → aiHandling (the AI answers them; nothing is on the owner);
 * 4. we spoke last on a thread the summary says is no longer open, with no stage to say otherwise → done;
 * 5. otherwise → waitingForThem (no messages at all included).
 */
export function outreachCardStatus(input: OutreachCardInput): OutreachCardStatus {
  if (input.stageGroup === 'done') return 'done';
  if (input.stageGroup === 'needsYou' || input.leadNeedsYou === true) return 'needsYou';
  const last = input.messages[input.messages.length - 1];
  if (last && !last.mine) return 'aiHandling';
  if (!input.stageGroup && last && input.awaitingReply === false) return 'done';
  return 'waitingForThem';
}

export type OutreachCardBadgeVariant = 'waiting-on-you' | 'in-progress' | 'waiting-on-a-reply' | 'done';

/** One colour per meaning, on every surface: orange needs you, blue the AI has it, grey waiting on them, green done. */
export const OUTREACH_CARD_STATUS: Record<
  OutreachCardStatus,
  { labelKey: string; variant: OutreachCardBadgeVariant; bucket: 'waiting' | 'inProgress' | 'done' }
> = {
  needsYou: { labelKey: 'outreachCard.status.needsYou', variant: 'waiting-on-you', bucket: 'waiting' },
  aiHandling: { labelKey: 'outreachCard.status.aiHandling', variant: 'in-progress', bucket: 'inProgress' },
  waitingForThem: { labelKey: 'outreachCard.status.waitingForThem', variant: 'waiting-on-a-reply', bucket: 'inProgress' },
  done: { labelKey: 'outreachCard.status.done', variant: 'done', bucket: 'done' },
};

function clean(text: string | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

function lastOf(messages: readonly OutreachCardMessage[], mine: boolean): OutreachCardMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && m.mine === mine && clean(m.text)) return m;
  }
  return undefined;
}

/** The row's one line: THEIR latest words; else what we sent, labelled as ours; else "No reply yet". */
export type OutreachCardSummary = { kind: 'theirs'; text: string } | { kind: 'ours'; text: string } | { kind: 'none' };

export function outreachCardSummary(messages: readonly OutreachCardMessage[]): OutreachCardSummary {
  const theirs = lastOf(messages, false);
  if (theirs) return { kind: 'theirs', text: clean(theirs.text) };
  const ours = lastOf(messages, true);
  return ours ? { kind: 'ours', text: clean(ours.text) } : { kind: 'none' };
}

/** The summary as a key + params, for a surface that renders it. */
export function outreachCardSummaryLine(summary: OutreachCardSummary): { text: string } | { key: string; params: Record<string, string> } {
  if (summary.kind === 'theirs') return { text: summary.text };
  if (summary.kind === 'ours') return { key: 'outreachCard.summary.ours', params: { text: summary.text } };
  return { key: 'outreachCard.summary.noReply', params: {} };
}

/**
 * The detail's subtitle — always true. `time` is the ISO instant the surface formats into `{time}`; a subtitle
 * that needs one and has none falls back to the status label rather than an empty "since".
 */
export function outreachCardSubtitle(
  status: OutreachCardStatus,
  messages: readonly OutreachCardMessage[],
): { key: string; time?: string } {
  const theirs = lastOf(messages, false);
  const ours = lastOf(messages, true);
  switch (status) {
    case 'needsYou':
      return theirs?.at ? { key: 'outreachCard.subtitle.needsYou', time: theirs.at } : { key: OUTREACH_CARD_STATUS.needsYou.labelKey };
    case 'aiHandling':
      return theirs?.at ? { key: 'outreachCard.subtitle.aiHandling', time: theirs.at } : { key: OUTREACH_CARD_STATUS.aiHandling.labelKey };
    case 'waitingForThem':
      if (!theirs) return { key: 'outreachCard.summary.noReply' };
      return ours?.at ? { key: 'outreachCard.subtitle.waitingForThem', time: ours.at } : { key: OUTREACH_CARD_STATUS.waitingForThem.labelKey };
    case 'done':
      return { key: OUTREACH_CARD_STATUS.done.labelKey };
  }
}

/**
 * Who the card is about: the name the sender's assistant resolved, else the contact book's, else the profile-link
 * lead's ("Visitor from Wix 3.0"). Empty when nobody named them — the surface then shows `contacts.row.unnamed`,
 * never the old "Recipient".
 */
export function outreachCardPerson(names: { recipientName?: string | null; contactName?: string | null; leadName?: string | null }): string {
  return clean(names.recipientName ?? undefined) || clean(names.contactName ?? undefined) || clean(names.leadName ?? undefined);
}

/** Where the card's actions go. Reply = the existing thread screen with its "Tell {agent}" composer; Open chat =
 *  the lead's owner-side conversation (where the Profile-links People row already opens). */
export interface OutreachCardActions {
  /** The contact book's person (or, for an address-only counterparty, its recipient) and the thread. */
  reply?: { contact: { personId: string } | { recipientId: string }; outreachId: string };
  openChat?: { conversationId: string };
}

export function outreachCardActions(input: {
  outreachId: string;
  personId?: string | null;
  recipientId?: string | null;
  conversationId?: string | null;
}): OutreachCardActions {
  const outreachId = clean(input.outreachId);
  const personId = clean(input.personId ?? undefined);
  const recipientId = clean(input.recipientId ?? undefined);
  const conversationId = clean(input.conversationId ?? undefined);
  const contact = personId ? { personId } : recipientId ? { recipientId } : null;
  return {
    ...(outreachId && contact ? { reply: { contact, outreachId } } : {}),
    ...(conversationId ? { openChat: { conversationId } } : {}),
  };
}

/** The stage dot's meaning, for its accessible name and tooltip (`StageDot`). One meaning per colour. */
export const STAGE_DOT_LABEL_KEY = {
  needsYou: 'outreach.dot.needsYou',
  scheduled: 'outreach.dot.scheduled',
  stuck: 'outreach.dot.stuck',
  handling: 'outreach.dot.handling',
} as const;
